import { useEffect, useRef, useCallback } from 'react';

const DISMISS_THRESHOLD = 80;
const VELOCITY_THRESHOLD = 0.3;
const DRAG_DEAD_ZONE = 6;


function preventDefault(e) {
    if (e.target && e.target.closest('.offcanvas, .modal-content')) return;
    e.preventDefault();
}

function lockBodyScroll() {
    document.addEventListener('wheel', preventDefault, { passive: false });
    document.addEventListener('touchmove', preventDefault, { passive: false });
}

function unlockBodyScroll() {
    document.removeEventListener('wheel', preventDefault);
    document.removeEventListener('touchmove', preventDefault);
}



export function useSwipeClose(show, onHide) {
    const handleRef = useRef(null);
    const state = useRef({
        startY: 0,
        currentY: 0,
        startTime: 0,
        dragging: false,
        didMove: false,
        slideEl: null,
        hideEl: null,
        backdrop: null,
        isClosing: false,
        pointerId: null,
    });


    useEffect(() => {
        if (!show) return;
        lockBodyScroll();
        return () => {
            unlockBodyScroll();
        };
    }, [show]);

    useEffect(() => {
        if (!show) return;

        function getOverlayEl() {
            const handle = handleRef.current;
            if (!handle) return null;
            return handle.closest('.offcanvas') || handle.closest('.modal');
        }

        let overlayEl = null;
        let originalTransform = '';
        let visualViewportHandler = null;

        function onFocusIn(e) {
            const tag = e.target?.tagName?.toLowerCase();
            if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;

            overlayEl = overlayEl || getOverlayEl();
            if (!overlayEl) return;

            originalTransform = overlayEl.style.transform || '';

            if (!window.visualViewport) return;

            visualViewportHandler = () => {
                if (!overlayEl) return;
                const offsetY =
                    window.innerHeight -
                    window.visualViewport.height -
                    window.visualViewport.offsetTop;
                overlayEl.style.transform = `translateY(-${Math.max(0, offsetY)}px)`;
                overlayEl.style.transition = 'none';
            };

            window.visualViewport.addEventListener('resize', visualViewportHandler);
            window.visualViewport.addEventListener('scroll', visualViewportHandler);
            visualViewportHandler();
        }

        function onFocusOut(e) {
            const tag = e.target?.tagName?.toLowerCase();
            if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;

            if (overlayEl && overlayEl.contains(e.relatedTarget)) return;

            if (visualViewportHandler) {
                window.visualViewport?.removeEventListener('resize', visualViewportHandler);
                window.visualViewport?.removeEventListener('scroll', visualViewportHandler);
                visualViewportHandler = null;
            }

            if (overlayEl) {
                overlayEl.style.transform = originalTransform;
                overlayEl.style.transition = '';
                originalTransform = '';
            }
        }

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);

        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            if (visualViewportHandler) {
                window.visualViewport?.removeEventListener('resize', visualViewportHandler);
                window.visualViewport?.removeEventListener('scroll', visualViewportHandler);
            }
        };
    }, [show]);


    const findElements = useCallback(() => {
        const handle = handleRef.current;
        if (!handle) return {};

        const offcanvas = handle.closest('.offcanvas');
        const modalContent = handle.closest('.modal-content');

        if (offcanvas) {
            return {
                slideEl: offcanvas,
                hideEl: offcanvas,
                backdrop: document.querySelector('.offcanvas-backdrop'),
            };
        }

        if (modalContent) {
            return {
                slideEl: modalContent,
                hideEl: handle.closest('.modal-dialog'),
                backdrop: document.querySelector('.modal-backdrop'),
            };
        }

        return {};
    }, []);

    const resetAll = useCallback(() => {
        const s = state.current;
        if (s.slideEl) {
            s.slideEl.style.transition = '';
            s.slideEl.style.transform = '';
            s.slideEl.style.willChange = '';
        }
        if (s.hideEl) {
            s.hideEl.style.display = '';
        }
        if (s.backdrop) {
            s.backdrop.style.transition = '';
            s.backdrop.style.opacity = '0';
            s.backdrop.style.display = '';
        }
        s.slideEl = null;
        s.hideEl = null;
        s.backdrop = null;
        s.dragging = false;
        s.didMove = false;
        s.isClosing = false;
        s.pointerId = null;
    }, []);

    useEffect(() => {
        const handle = handleRef.current;
        if (!show || !handle) return;

        const s = state.current;
        s.isClosing = false;

        function onPointerDown(e) {
            if (s.isClosing) return;

            s.dragging = true;
            s.didMove = false;
            s.startY = e.clientY;
            s.currentY = e.clientY;
            s.startTime = Date.now();
            s.pointerId = e.pointerId;

            handle.setPointerCapture(e.pointerId);

            const els = findElements();
            s.slideEl = els.slideEl || null;
            s.hideEl = els.hideEl || null;
            s.backdrop = els.backdrop || null;

            if (s.slideEl) {
                s.slideEl.style.transition = 'none';
                s.slideEl.style.willChange = 'transform';
            }
        }

        function onPointerMove(e) {
            if (!s.dragging || !s.slideEl || s.isClosing) return;

            const deltaY = e.clientY - s.startY;
            s.currentY = e.clientY;

            if (Math.abs(deltaY) < DRAG_DEAD_ZONE && !s.didMove) return;

            s.didMove = true;

            if (deltaY <= 0) {
                s.slideEl.style.transform = 'translateY(0)';
                return;
            }

            e.preventDefault();
            s.slideEl.style.transform = `translateY(${deltaY}px)`;

            if (s.backdrop) {
                s.backdrop.style.opacity = String(Math.max(0, 0.5 - deltaY / 500));
            }
        }

        function onPointerUp() {
            if (!s.dragging || s.isClosing) {
                s.dragging = false;
                return;
            }

            if (s.pointerId !== null) {
                try { handle.releasePointerCapture(s.pointerId); } catch (_) { }
            }

            s.dragging = false;

            if (!s.didMove) {
                if (s.slideEl) {
                    s.slideEl.style.transition = '';
                    s.slideEl.style.transform = '';
                    s.slideEl.style.willChange = '';
                }
                return;
            }

            const deltaY = s.currentY - s.startY;
            const elapsed = Date.now() - s.startTime;
            const velocity = elapsed > 0 ? deltaY / elapsed : 0;
            const shouldClose = deltaY > DISMISS_THRESHOLD || velocity > VELOCITY_THRESHOLD;

            if (shouldClose && s.slideEl) {
                s.isClosing = true;

                s.slideEl.style.transition = 'transform 0.25s cubic-bezier(0.4, 0, 1, 1)';
                s.slideEl.style.transform = 'translateY(100vh)';

                if (s.backdrop) {
                    s.backdrop.style.transition = 'opacity 0.25s ease';
                    s.backdrop.style.opacity = '0';
                }

                setTimeout(() => {
                    const offcanvasEl = handle.closest('.offcanvas');
                    const modalEl = handle.closest('.modal');
                    if (offcanvasEl) offcanvasEl.classList.remove('show', 'showing');
                    if (modalEl) modalEl.classList.remove('show', 'showing');

                    if (s.backdrop) s.backdrop.style.display = 'none';

                    document.body.classList.remove('offcanvas-open');

                    onHide();

                    requestAnimationFrame(() => resetAll());
                }, 250);

            } else if (s.slideEl) {
                s.slideEl.style.transition = 'transform 0.3s cubic-bezier(0.2, 0.9, 0.3, 1)';
                s.slideEl.style.transform = 'translateY(0)';

                if (s.backdrop) {
                    s.backdrop.style.transition = 'opacity 0.3s ease';
                    s.backdrop.style.opacity = '';
                }

                setTimeout(() => {
                    if (s.slideEl) {
                        s.slideEl.style.transition = '';
                        s.slideEl.style.transform = '';
                        s.slideEl.style.willChange = '';
                    }
                    s.didMove = false;
                }, 320);
            }
        }

        function onPointerCancel() {
            if (!s.dragging) return;
            s.dragging = false;
            s.didMove = false;
            if (s.slideEl) {
                s.slideEl.style.transition = 'transform 0.3s ease';
                s.slideEl.style.transform = 'translateY(0)';
                setTimeout(() => {
                    if (s.slideEl) {
                        s.slideEl.style.transition = '';
                        s.slideEl.style.transform = '';
                        s.slideEl.style.willChange = '';
                    }
                }, 320);
            }
        }

        handle.addEventListener('pointerdown', onPointerDown);
        handle.addEventListener('pointermove', onPointerMove);
        handle.addEventListener('pointerup', onPointerUp);
        handle.addEventListener('pointercancel', onPointerCancel);

        return () => {
            handle.removeEventListener('pointerdown', onPointerDown);
            handle.removeEventListener('pointermove', onPointerMove);
            handle.removeEventListener('pointerup', onPointerUp);
            handle.removeEventListener('pointercancel', onPointerCancel);
            resetAll();
        };
    }, [show, onHide, findElements, resetAll]);

    return handleRef;
}